import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { DEFAULT_PRICE_CURRENCY } from "shared/constants/app-constants"
import { formatPrice } from "shared/lib/priceUtils"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import "./styles/_formatted-price.scss"

export interface FormattedPriceProps {
  price?: number | null
  currency?: string
  isLoading?: boolean
  isPriceServiceAvailable?: boolean
  unavailabilityMessage?: string
  className?: string
  "data-testid"?: string
}

export const FormattedPrice = React.memo(
  React.forwardRef<HTMLSpanElement, FormattedPriceProps>(
    (
      {
        price,
        currency = DEFAULT_PRICE_CURRENCY,
        isLoading = false,
        isPriceServiceAvailable = true,
        unavailabilityMessage,
        className,
        ...rest
      },
      ref
    ) => {
      const { t } = useTranslation()

      const formattedPrice = useMemo(() => {
        if (price === null || price === undefined) {
          return t("Price.NoPriceAvailable") as string
        }
        return formatPrice({ price, currency })
      }, [price, currency, t])

      if (isLoading) {
        return (
          <span
            {...rest}
            ref={ref}
            className={classNames(
              "formatted-price",
              "formatted-price--loading",
              className
            )}
          >
            <Skeleton />
          </span>
        )
      }

      if (!isPriceServiceAvailable) {
        return (
          <span
            {...rest}
            ref={ref}
            className={classNames(
              "formatted-price",
              "formatted-price--unavailable",
              className
            )}
          >
            <Icon size={IconSize.SMALL} name={IconName.CLOSE_CIRCLE} />
            {unavailabilityMessage || (t("Price.ServiceUnavailable") as string)}
          </span>
        )
      }

      return (
        <span
          {...rest}
          ref={ref}
          className={classNames("formatted-price", className)}
        >
          {formattedPrice}
        </span>
      )
    }
  )
)
